import { createPortal } from 'react-dom'
import type { ComponentType, ReactNode } from 'react'
import { useModal } from '../modal/use-modal'
import { Button, H4, Text, CloseLgIcon } from '@/components/atoms'
import {
  CircleCheckSolidIcon,
  CircleWarningFilledIcon,
  TriangleWarningCircleIcon,
  InfoIconFilled,
} from '@/components/atoms/icons/warning'

export type ActionModalVariant =
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'loading'
  | 'normal'

export interface ActionModalAction {
  label: string
  onClick?: () => void
  loading?: boolean
  disabled?: boolean
}

type IconComponent = ComponentType<{ className?: string }>

// Per-variant icon shown in the top-left corner — the only thing that differs
// between variants. `color` is the `currentColor` the icon is tinted with.
// `error`, `warning` and `info` are filled badges that bake their own color, so
// they leave `color` empty. `loading` renders an inline spinner; `normal` shows
// no icon. Override any of them per instance via the `icon` prop.
const VARIANT_ICON: Record<
  ActionModalVariant,
  { icon?: IconComponent; color: string }
> = {
  success: { icon: CircleCheckSolidIcon, color: 'text-success-base' },
  error: { icon: CircleWarningFilledIcon, color: '' },
  warning: { icon: TriangleWarningCircleIcon, color: '' },
  info: { icon: InfoIconFilled, color: '' },
  loading: { icon: undefined, color: 'text-indigo-base' },
  normal: { icon: undefined, color: '' },
}

interface ActionModalProps {
  isOpen: boolean
  variant?: ActionModalVariant
  title?: string
  description?: string
  /** Primary (filled) action. When omitted the button is not rendered. */
  primaryAction?: ActionModalAction
  /** Secondary (outline) action. When omitted the button is not rendered. */
  secondaryAction?: ActionModalAction
  onClose?: () => void
  showClose?: boolean
  /** Override the variant's top-left icon. */
  icon?: IconComponent
  /** Extra content rendered below the description. */
  children?: ReactNode
  className?: string
}

export function ActionModal({
  isOpen,
  variant = 'normal',
  title,
  description,
  primaryAction,
  secondaryAction,
  onClose = () => {},
  showClose = true,
  icon,
  children,
  className = '',
}: ActionModalProps) {
  const { mounted, visible } = useModal(isOpen, onClose)

  const config = VARIANT_ICON[variant]
  const ResolvedIcon = icon ?? config.icon
  const hasActions = Boolean(primaryAction || secondaryAction)

  if (!mounted) return <></>

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-(--z-modal) bg-(--overlay-bg) backdrop-blur-sm transition-opacity duration-300 ${
          visible
            ? 'pointer-events-auto opacity-100'
            : 'pointer-events-none opacity-0'
        }`}
        onClick={onClose}
        aria-hidden="true"
        data-testid="action-modal-backdrop"
      />
      {/* Panel container */}
      <div
        className={`fixed inset-0 z-(--z-modal) flex items-center justify-center p-4 ${
          !visible ? 'pointer-events-none' : ''
        }`}
        role="dialog"
        aria-modal="true"
        onClick={onClose}
      >
        {/* Panel */}
        <div
          className={`relative flex w-82 max-w-[calc(100vw-2rem)] flex-col gap-8 overflow-hidden rounded-2xl bg-white px-6 py-8 transition duration-200 md:w-123.5 md:max-w-123.5 md:p-8 ${
            visible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
          } ${className}`}
          onClick={(e) => e.stopPropagation()}
        >
          {showClose && (
            <button
              type="button"
              className="absolute top-5 right-5 z-10"
              onClick={onClose}
              aria-label="Close"
              data-testid="action-modal-close-button"
            >
              <CloseLgIcon className="text-indigo-base h-4 w-4 stroke-2" />
            </button>
          )}

          {/* Content */}
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4">
              {variant === 'loading' ? (
                <span className="loading loading-spinner text-indigo-base size-10.5" />
              ) : ResolvedIcon ? (
                <ResolvedIcon
                  className={`size-10.5 shrink-0 ${config.color}`}
                />
              ) : (
                <></>
              )}

              {title && (
                <H4 className="pr-8 text-xl leading-6 text-neutral-900 md:text-2xl md:leading-8">
                  {title}
                </H4>
              )}
            </div>

            {description && (
              <Text className="leading-5 text-neutral-600">{description}</Text>
            )}

            {children}
          </div>

          {/* Actions */}
          {hasActions && (
            <div className="flex flex-col items-stretch gap-4 md:flex-row md:items-center md:justify-end">
              {secondaryAction && (
                <Button
                  variant="secondary"
                  className="md:w-auto"
                  fullWidth
                  loading={secondaryAction.loading}
                  disabled={secondaryAction.disabled}
                  onClick={secondaryAction.onClick}
                >
                  {secondaryAction.label}
                </Button>
              )}
              {primaryAction && (
                <Button
                  variant="primary"
                  className="md:w-auto"
                  fullWidth
                  loading={primaryAction.loading}
                  disabled={primaryAction.disabled}
                  onClick={primaryAction.onClick}
                >
                  {primaryAction.label}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body,
  )
}
